import React, { useEffect, useMemo, useState } from "react";
import { FileText, Trash2 } from "lucide-react";
import FlowDiagram from "./FlowDiagram";
import InvoiceEditor from "./InvoiceEditor";
import { apiService } from "../../services/api";
import {
  formatMoney,
  getConsumedQuantities,
  getInvoiceAmountTotals,
  getLineAmount,
  parseDecimal,
  roundMoney,
} from "./billingMath";

const emptyNewLine = () => ({
  name: "",
  unit_price: "",
  quantity: "1",
  description: "",
  purchase_budget_eur: "",
});

export default function OrderPanel({
  batch,
  clients,
  showError,
  onPatchBatch,
  onDeleteBatch,
  onAddLine,
  onPatchLine,
  onDeleteLine,
  onCreateInvoice,
  onUpdateInvoice,
  onUpdateInvoiceBillability,
  onDeleteInvoice,
}) {
  const [newLine, setNewLine] = useState(emptyNewLine);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);

  const lines = useMemo(() => batch.lines || [], [batch.lines]);
  const cashEvents = useMemo(() => batch.cash_events || [], [batch.cash_events]);
  const invoiceEvents = useMemo(
    () => cashEvents.filter((event) => event.direction !== "outflow" && event.status !== "Annule"),
    [cashEvents],
  );
  const consumedByLine = useMemo(() => getConsumedQuantities(cashEvents, lines), [cashEvents, lines]);

  const totalOrderAmount = useMemo(() => lines.reduce((sum, line) => sum + getLineAmount(line), 0), [lines]);
  const invoiceTotals = useMemo(() => getInvoiceAmountTotals(invoiceEvents), [invoiceEvents]);
  const totalInvoicedAmount = roundMoney(invoiceTotals.invoiced);
  const plannedInvoiceAmount = roundMoney(invoiceTotals.planned);
  const linkedInvoiceAmount = roundMoney(invoiceTotals.linked);
  const unlinkedInvoiceAmount = Math.max(roundMoney(totalOrderAmount - linkedInvoiceAmount), 0);
  const remainingInvoiceAmount = Math.max(roundMoney(totalOrderAmount - totalInvoicedAmount), 0);

  // Itérations des projets du même client (pour ancrer une facture sur une itération)
  const [clientIterations, setClientIterations] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  useEffect(() => {
    let active = true;
    if (!batch?.id || !batch.client_id) {
      setClientIterations([]);
      setClientProjects([]);
      return undefined;
    }
    Promise.all([
      apiService.get(`/billing-batches/${batch.id}/client-iterations`),
      apiService.get(`/billing-batches/${batch.id}/client-projects`),
    ])
      .then(([iterationsData, projectsData]) => {
        if (!active) return;
        setClientIterations(Array.isArray(iterationsData) ? iterationsData : []);
        setClientProjects(Array.isArray(projectsData) ? projectsData : []);
      })
      .catch(() => {
        if (active) {
          setClientIterations([]);
          setClientProjects([]);
        }
      });
    return () => {
      active = false;
    };
  }, [batch?.id, batch.client_id]);

  const iterationOptions = useMemo(
    () =>
      clientIterations.map((iteration) => ({
        id: iteration.id,
        label: `${iteration.project_number ? `${iteration.project_number} - ` : ""}${iteration.project_name || "Projet"} / ${iteration.name || `Iteration ${iteration.id}`}`,
        start_date: iteration.start_date || null,
        end_date: iteration.end_date || null,
        project_id: iteration.project_id,
        project_name: iteration.project_name || "",
        project_number: iteration.project_number || "",
        project_status: iteration.project_status || null,
        project_invoice_count: Number(iteration.project_invoice_count || 0),
        iteration_invoice_count: Number(iteration.iteration_invoice_count || 0),
      })),
    [clientIterations],
  );

  const projectOptions = useMemo(
    () =>
      clientProjects.map((project) => ({
        id: project.id,
        name: project.name || "Projet",
        project_number: project.project_number || "",
        status: project.status || null,
        invoice_count: Number(project.invoice_count || 0),
      })),
    [clientProjects],
  );

  const clientPaymentTerms = useMemo(() => {
    const client = clients.find((item) => item.id === batch.client_id);
    return client?.payment_terms || null;
  }, [clients, batch.client_id]);

  const handleAddLine = (event) => {
    event.preventDefault();
    const quantity = parseDecimal(newLine.quantity);
    const unitPrice = parseDecimal(newLine.unit_price);
    const purchaseBudget = parseDecimal(newLine.purchase_budget_eur);
    if (!newLine.name.trim() || !Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
      showError("Ligne invalide");
      return;
    }
    onAddLine({
      name: newLine.name.trim(),
      unit_price: unitPrice,
      quantity,
      description: newLine.description.trim() || null,
      purchase_budget_eur: Number.isFinite(purchaseBudget) ? purchaseBudget : null,
    }).then((ok) => {
      if (ok) setNewLine(emptyNewLine());
    });
  };

  const handleCreateInvoice = async (payload) => {
    const ok = await onCreateInvoice(payload);
    if (ok) setShowInvoiceForm(false);
    return ok;
  };

  const stats = [
    { label: "Commande", value: formatMoney(totalOrderAmount) },
    { label: "Facturé", value: formatMoney(totalInvoicedAmount) },
    {
      label: "Prévu",
      value: formatMoney(plannedInvoiceAmount),
      accent: plannedInvoiceAmount > 0.01 ? "text-amber-700" : "text-slate-950",
    },
    {
      label: "Non lié",
      value: formatMoney(unlinkedInvoiceAmount),
      accent: unlinkedInvoiceAmount <= 0.01 ? "text-emerald-700" : "text-slate-950",
    },
    {
      label: "Reste à facturer",
      value: formatMoney(remainingInvoiceAmount),
      accent: remainingInvoiceAmount <= 0.01 ? "text-emerald-700" : "text-amber-700",
    },
  ];

  return (
    <div className="space-y-4 border-t border-slate-100 bg-slate-50/60 px-3 py-4 sm:px-4">
      {/* Meta edit */}
      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm md:grid-cols-[1fr_1fr_1fr_auto] md:items-end">
        <label className="text-xs font-medium text-slate-500">
          Reference
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
            defaultValue={batch.reference || ""}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value && value !== batch.reference) onPatchBatch({ reference: value });
            }}
          />
        </label>
        <label className="text-xs font-medium text-slate-500">
          Objet
          <input
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
            defaultValue={batch.name || ""}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value !== (batch.name || "")) onPatchBatch({ name: value || null });
            }}
          />
        </label>
        <label className="text-xs font-medium text-slate-500">
          Client
          <select
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900"
            value={batch.client_id || ""}
            onChange={(event) => onPatchBatch({ client_id: event.target.value ? Number(event.target.value) : null })}
          >
            <option value="">Non renseigne</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={onDeleteBatch}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50"
        >
          <Trash2 className="h-4 w-4" />
          Supprimer
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <div className="text-xs text-slate-500">{stat.label}</div>
            <div className={`text-lg font-semibold ${stat.accent || "text-slate-950"}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Flow diagram */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <FlowDiagram
          lines={lines}
          invoiceEvents={invoiceEvents}
          cashEvents={cashEvents}
          consumedByLine={consumedByLine}
          projectOptions={projectOptions}
          iterationOptions={iterationOptions}
          clientPaymentTerms={clientPaymentTerms}
          newLine={newLine}
          setNewLine={setNewLine}
          onAddLine={handleAddLine}
          onPatchLine={onPatchLine}
          onDeleteLine={onDeleteLine}
          onUpdateInvoice={onUpdateInvoice}
          onUpdateInvoiceBillability={onUpdateInvoiceBillability}
          onDeleteInvoice={onDeleteInvoice}
          showError={showError}
        />
      </div>

      {/* New invoice */}
      {showInvoiceForm ? (
        <InvoiceEditor
          mode="create"
          lines={lines}
          cashEvents={cashEvents}
          projectOptions={projectOptions}
          iterationOptions={iterationOptions}
          clientPaymentTerms={clientPaymentTerms}
          onCreate={handleCreateInvoice}
          onCancel={() => setShowInvoiceForm(false)}
          showError={showError}
        />
      ) : (
        <button
          type="button"
          onClick={() => setShowInvoiceForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          <FileText className="h-4 w-4" />
          Nouvelle facture
        </button>
      )}
    </div>
  );
}
